import React, { useCallback, useEffect, useRef, useState } from 'react'
import Dropzone from 'react-dropzone'
import { toast } from 'react-toastify'

import {
  Button,
  Chip,
  Dialog,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  CircularProgress,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { filesize } from 'filesize'
import { DisputesService } from 'services/disputes/disputesService'
import {
  DISPUTE_STATUS_LABEL,
  getDisputeChipSx
} from 'services/disputes/disputeStatus'
import {
  DefensePdf,
  DisputeDetail,
  DisputeDocument
} from 'services/disputes/types'

import IconContainer from 'components/IconContainer/IconContainer'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import {
  DETAIL_MODAL_HEIGHT,
  DETAIL_MODAL_MAX_WIDTH
} from '../../ModalBalance.constants'

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/tiff'
]
const MAX_BYTES = 3 * 1024 * 1024

const DEFENSE_ARTICLE_URL =
  'https://ajuda.basedeclientes.com.br/pt-BR/articles/15556002-contestacoes'

// Normas exigidas pela Zoop para os arquivos da defesa. Os documentos anexados
// são consolidados em um único PDF que precisa respeitar esses limites.
const ZOOP_FILE_RULES: { label: string; description: string }[] = [
  {
    label: 'Formatos aceitos',
    description: 'PDF, JPG ou TIFF.'
  },
  {
    label: 'Tamanho por arquivo',
    description: 'Até 3MB cada.'
  },
  {
    label: 'PDF de defesa',
    description: 'O arquivo final consolidado não pode passar de 7MB.'
  },
  {
    label: 'Páginas',
    description: 'No máximo 10 páginas no PDF de defesa.'
  }
]

const ZoopRulesTooltip = () => (
  <Tooltip
    arrow
    enterTouchDelay={0}
    title={
      <Box>
        {ZOOP_FILE_RULES.map((rule) => (
          <p key={rule.label}>
            <b>{rule.label}: </b>
            {rule.description}
          </p>
        ))}
      </Box>
    }
  >
    <Box component="span" display="inline-flex" alignItems="center">
      <Icon
        icon="info"
        sx={{ fontSize: '1.2rem', color: theme.palette.neutral[600] }}
      />
    </Box>
  </Tooltip>
)

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" gap={1.25} alignItems="end" margin="10px 0px 10px 0px">
    <Typography variant="smLight" color="neutral.500">
      {label}
    </Typography>
    <Box
      sx={{
        flexGrow: 1,
        borderTop: `0.5px dashed ${theme.palette.neutral[600]}`,
        alignSelf: 'center',
        marginTop: '2px'
      }}
    />
    <Typography variant="smBold" component="span">
      {value}
    </Typography>
  </Box>
)

// tom frio-neutro alinhado à paleta (mint sobre neutro), discreto como manda
// o princípio "dados antes de decoração"
const SKELETON_BG = theme.palette.neutral[700]

const SkeletonRow = ({
  labelWidth,
  valueWidth,
  chip = false
}: {
  labelWidth: number
  valueWidth: number
  chip?: boolean
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    margin="10px 0px"
  >
    <Skeleton
      variant="text"
      width={labelWidth}
      height={16}
      sx={{ bgcolor: SKELETON_BG }}
    />
    <Skeleton
      variant={chip ? 'rounded' : 'text'}
      width={valueWidth}
      height={chip ? 22 : 16}
      sx={{ bgcolor: SKELETON_BG, borderRadius: chip ? '11px' : undefined }}
    />
  </Box>
)

const SkeletonDocumentRow = () => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 1,
      border: `1px solid ${theme.palette.neutral[700]}`
    }}
    gap={1}
  >
    <Skeleton
      variant="rounded"
      width={18}
      height={18}
      sx={{ bgcolor: SKELETON_BG }}
    />
    <Box flexGrow={1}>
      <Skeleton
        variant="text"
        width="55%"
        height={14}
        sx={{ bgcolor: SKELETON_BG }}
      />
      <Skeleton
        variant="text"
        width={56}
        height={12}
        sx={{ bgcolor: SKELETON_BG }}
      />
    </Box>
  </Stack>
)

const DetailSkeleton = () => (
  <Box px={0.5}>
    <Box
      sx={{
        border: `1px solid ${theme.palette.neutral[700]}`,
        borderRadius: 1,
        p: 1.5,
        mt: 1.5
      }}
    >
      <SkeletonRow labelWidth={56} valueWidth={92} chip />
      <SkeletonRow labelWidth={56} valueWidth={150} />
      <SkeletonRow labelWidth={44} valueWidth={96} />
      <SkeletonRow labelWidth={48} valueWidth={112} />
    </Box>

    <Box mt={2}>
      <Skeleton
        variant="text"
        width={132}
        height={18}
        sx={{ bgcolor: SKELETON_BG, mb: 1 }}
      />
      <Stack spacing={1}>
        <SkeletonDocumentRow />
        <SkeletonDocumentRow />
      </Stack>
    </Box>
  </Box>
)

interface Props {
  uuid: string | null
  onClose: () => void
  onOpenBillingDetail?: (billingUuid: string) => void
}

export const DisputeDetailDrawer = ({
  uuid,
  onClose,
  onOpenBillingDetail
}: Props) => {
  const [detail, setDetail] = useState<DisputeDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const refreshRef = useRef<NodeJS.Timeout | null>(null)

  const fetchDetail = useCallback(
    async (silent = false) => {
      if (!uuid) return
      if (!silent) setLoading(true)
      try {
        const res = await DisputesService.show(uuid)
        setDetail(res.record)
      } catch {
        // silently handled
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [uuid]
  )

  useEffect(() => {
    if (uuid) {
      fetchDetail()
    } else {
      setDetail(null)
    }
    return () => {
      if (refreshRef.current) clearTimeout(refreshRef.current)
    }
  }, [uuid, fetchDetail])

  const scheduleRefresh = () => {
    if (refreshRef.current) clearTimeout(refreshRef.current)
    refreshRef.current = setTimeout(() => fetchDetail(true), 8000)
  }

  const handleDrop = async (files: File[]) => {
    if (!uuid || !detail) return

    const valid = files.filter(
      (f) => ALLOWED_MIME_TYPES.includes(f.type) && f.size <= MAX_BYTES
    )
    if (valid.length === 0) return

    setUploading(true)
    try {
      const results = await Promise.allSettled(
        valid.map((f) => DisputesService.uploadDocument(uuid, f))
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      const ok = results.length - failed

      if (ok > 0) {
        toast.success(
          ok === 1
            ? 'Documento adicionado com sucesso.'
            : `${ok} documentos adicionados com sucesso.`
        )
      }
      if (failed > 0) {
        toast.error(
          failed === 1
            ? 'Um documento não pôde ser enviado.'
            : `${failed} documentos não puderam ser enviados.`
        )
      }
      await fetchDetail(true)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (doc: DisputeDocument) => {
    if (!uuid) return
    setDeletingId(doc.signed_id)
    try {
      await DisputesService.deleteDocument(uuid, doc.signed_id)
      toast.success('Documento removido.')
      await fetchDetail(true)
    } catch {
      toast.error('Erro ao remover o documento.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleSubmit = async () => {
    if (!uuid) return
    setConfirmOpen(false)
    setSubmitting(true)
    try {
      await DisputesService.submit(uuid)
      toast.success('Defesa enviada com sucesso.')
      await fetchDetail(true)
      scheduleRefresh()
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Erro ao enviar a defesa.'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const isOpen = !!uuid
  const canEdit = detail?.status === 'awaiting_documents'

  return (
    <Modal
      handleClose={onClose}
      open={isOpen}
      size="large"
      width="100%"
      maxWidth={DETAIL_MODAL_MAX_WIDTH}
      height={DETAIL_MODAL_HEIGHT}
    >
      <ModalHeader onClose={onClose}>
        <Stack direction="row" alignItems="center" gap={2}>
          <IconContainer icon="gavel" />
          <Typography variant="mdBold">Detalhes da Contestação</Typography>
        </Stack>
      </ModalHeader>

      <ModalContent>
        {loading && <DetailSkeleton />}

        {!loading && !detail && (
          <Typography variant="smLight" color="neutral.500" px={0.5}>
            Não foi possível carregar os detalhes.
          </Typography>
        )}

        {!loading && detail && (
          <Box px={0.5}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.neutral[700]}`,
                borderRadius: 1,
                p: 1.5,
                mt: 1.5
              }}
            >
              <Row
                label="Status"
                value={
                  <Chip
                    variant="payment"
                    label={DISPUTE_STATUS_LABEL[detail.status]}
                    sx={getDisputeChipSx(detail.status)}
                  />
                }
              />

              {detail.customer && (
                <Row label="Cliente" value={detail.customer.name} />
              )}

              {detail.billing && (
                <Row
                  label="Valor"
                  value={formatMoney(Number(detail.billing.amount_billed))}
                />
              )}

              {detail.dispute_deadline_at &&
                detail.status === 'awaiting_documents' && (
                  <Row
                    label="Prazo"
                    value={
                      <Typography
                        variant="smBold"
                        component="span"
                        color={
                          (detail.days_remaining ?? 0) <= 1
                            ? theme.palette.error[300]
                            : 'text.primary'
                        }
                      >
                        {detail.days_remaining === 0
                          ? 'Hoje'
                          : detail.days_remaining === 1
                          ? '1 dia restante'
                          : `${detail.days_remaining} dias restantes`}
                      </Typography>
                    }
                  />
                )}
            </Box>

            {(detail.documents.length > 0 || canEdit) && (
              <Box mt={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  mb={1}
                  width="fit-content"
                >
                  <Typography variant="smBold">
                    Documentos{' '}
                    {detail.documents.length > 0 &&
                      `(${detail.documents.length})`}
                  </Typography>
                  <ZoopRulesTooltip />
                </Stack>

                {detail.documents.length === 0 && (
                  <Typography variant="smLight" color="neutral.500">
                    Nenhum documento anexado ainda.
                  </Typography>
                )}

                <Stack spacing={1}>
                  {detail.documents.map((doc) => (
                    <Stack
                      key={doc.signed_id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.neutral[700]}`,
                        background: theme.palette.neutral[900]
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        minWidth={0}
                      >
                        <Icon
                          icon="description"
                          colorSX={theme.palette.neutral[400]}
                          width="18px"
                          height="18px"
                        />
                        <Box minWidth={0}>
                          <Typography
                            variant="xsBold"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}
                          >
                            {doc.filename}
                          </Typography>
                          <Typography variant="xsLight" color="neutral.500">
                            {filesize(doc.size)}
                          </Typography>
                        </Box>
                      </Stack>
                      {canEdit &&
                        (deletingId === doc.signed_id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            title="Remover documento"
                          >
                            <Box
                              component="button"
                              onClick={() => handleDelete(doc)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: theme.palette.error[300],
                                p: 0.5
                              }}
                            >
                              <Icon
                                icon="delete"
                                colorSX={theme.palette.error[300]}
                                width="16px"
                                height="16px"
                              />
                            </Box>
                          </Tooltip>
                        ))}
                    </Stack>
                  ))}
                </Stack>

                {canEdit && (
                  <Box mt={1.5}>
                    <Dropzone
                      onDrop={handleDrop}
                      accept={{
                        'application/pdf': ['.pdf'],
                        'image/jpeg': ['.jpg', '.jpeg'],
                        'image/tiff': ['.tif', '.tiff']
                      }}
                      maxSize={MAX_BYTES}
                      multiple
                      disabled={uploading}
                      onDropRejected={() =>
                        toast.error(
                          'Arquivo inválido. Verifique o tipo e tamanho (máx. 3MB).'
                        )
                      }
                    >
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <Box
                          {...getRootProps()}
                          sx={{
                            border: `2px dashed ${
                              isDragActive
                                ? theme.palette.primary[200]
                                : theme.palette.neutral[600]
                            }`,
                            borderRadius: 2,
                            p: 2.5,
                            textAlign: 'center',
                            cursor: uploading ? 'default' : 'pointer',
                            background: isDragActive
                              ? `${theme.palette.primary[200]}0d`
                              : 'transparent',
                            transition: 'border-color 0.2s, background 0.2s'
                          }}
                        >
                          <input {...getInputProps()} />
                          {uploading ? (
                            <Stack alignItems="center" gap={1}>
                              <CircularProgress size={24} />
                              <Typography variant="smLight" color="neutral.500">
                                Enviando...
                              </Typography>
                            </Stack>
                          ) : (
                            <Stack alignItems="center" gap={0.5}>
                              <Icon
                                icon="uploadFile"
                                colorSX={theme.palette.neutral[400]}
                                width="32px"
                                height="32px"
                              />
                              <Typography variant="smBold">
                                {isDragActive
                                  ? 'Solte os arquivos aqui'
                                  : 'Clique ou arraste arquivos'}
                              </Typography>
                              <Typography variant="xsLight" color="neutral.500">
                                PDF, JPG ou TIFF — máx. 3MB cada
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      )}
                    </Dropzone>

                    <Typography
                      variant="xsLight"
                      color="neutral.500"
                      display="block"
                      mt={1}
                    >
                      Anexe documentos que comprovem a prestação do serviço,
                      como notas fiscais, contratos e conversas com o cliente. A
                      defesa é limitada a <strong>10 páginas</strong> no total;
                      o que exceder esse limite não será enviado.{' '}
                      <Link
                        href={DEFENSE_ARTICLE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primary[200],
                          fontWeight: 600,
                          textDecorationColor: theme.palette.primary[200]
                        }}
                      >
                        Saiba como montar a defesa
                      </Link>
                      .
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <DefensePdfSection
              pdf={detail.defense_pdf}
              isPending={detail.status === 'processing_pdf'}
            />
          </Box>
        )}
      </ModalContent>

      {detail && (canEdit || (detail.billing?.uuid && onOpenBillingDetail)) && (
        <ModalFooter>
          {detail.billing?.uuid && onOpenBillingDetail && (
            <Button
              variantButton="neutral"
              onClick={() => onOpenBillingDetail(detail.billing?.uuid ?? '')}
            >
              Ver cobrança
            </Button>
          )}
          {canEdit && (
            <Button
              disabled={detail.documents.length === 0 || submitting}
              loading={submitting}
              onClick={() => setConfirmOpen(true)}
            >
              Enviar defesa
            </Button>
          )}
        </ModalFooter>
      )}

      <Dialog
        icon="description"
        title=""
        description="Confirma o envio dos documentos para análise? Após o envio não será possível adicionar ou remover documentos."
        setIsOpenDialog={() => setConfirmOpen(false)}
        isOpenDialog={confirmOpen}
      >
        <Button
          variantButton="neutral"
          onClick={() => setConfirmOpen(false)}
          fullWidth
        >
          Voltar
        </Button>
        <Button loading={submitting} onClick={handleSubmit} fullWidth>
          Confirmar
        </Button>
      </Dialog>
    </Modal>
  )
}

const DefensePdfSection = ({
  pdf,
  isPending
}: {
  pdf: DefensePdf | null
  isPending: boolean
}) => {
  if (!pdf && !isPending) return null

  return (
    <Box mt={2}>
      <Typography variant="smBold" mb={1}>
        PDF de defesa
      </Typography>
      {isPending && !pdf && (
        <Stack gap={1}>
          <Typography variant="smLight" color="neutral.500">
            Gerando PDF de defesa...
          </Typography>
          <LinearProgress />
        </Stack>
      )}
      {pdf && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1,
            border: `1px solid ${theme.palette.neutral[700]}`,
            background: theme.palette.neutral[900]
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Icon
              icon="description"
              colorSX={theme.palette.neutral[400]}
              width="18px"
              height="18px"
            />
            <Box>
              <Typography variant="xsBold">{pdf.filename}</Typography>
              <Typography variant="xsLight" color="neutral.500">
                {filesize(pdf.size)}
              </Typography>
            </Box>
          </Stack>
          <Tooltip enterTouchDelay={0} arrow title="Baixar PDF de defesa">
            <Box
              component="button"
              onClick={() => window.open(pdf.url, '_blank', 'noopener')}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                p: 0.5
              }}
            >
              <Icon
                icon="download"
                colorSX={theme.palette.primary[200]}
                width="16px"
                height="16px"
              />
            </Box>
          </Tooltip>
        </Stack>
      )}
    </Box>
  )
}
